import { Transition } from "@headlessui/react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ApiService } from "../api/services.gen";
import { checkAndRedirectByRole } from "../utils/authUtils";
import { getUserDetails } from "../utils/getUserDetails";

import { useAlert } from "./AlertProvider";
import Navbar from "./Navbar";
import { signupSchema } from "./schemas/signupSchema";

export default function RecSignup() {
  const location = useLocation();
  const recRole = location.state?.recRole || "recruiter";
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      username: "",
      email: "",
      password: "",
      role: recRole,
    },
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const showAlert = useAlert();

  useEffect(() => {
    const verifySession = async () => {
      const id = Cookies.get("id");
      const role = Cookies.get("role");
      if (id || role) {
        try {
          await getUserDetails();
          checkAndRedirectByRole(navigate);
        } catch (error) {
          Cookies.remove("id");
          Cookies.remove("role");
          Cookies.remove("csrftoken");
        }
      }
    };
    verifySession();
  }, [navigate]);

  const onSubmit = async ({ username, email, password, role }) => {
    setLoading(true);
    try {
      const payload = {
        requestBody: {
          email,
          password,
          name: username,
          role: recRole,
          phone_number: "",
          address: "",
        },
      };
      await ApiService.apiSignupCreate(payload);
      // After successful signup, log the user in
      const loginPayload = {
        requestBody: {
          email,
          password,
        },
      };
      const loginRes = await ApiService.apiLoginCreate(loginPayload);
      const userId = loginRes.id;
      Cookies.set("id", userId, { expires: 7 });
      Cookies.set("role", recRole, { expires: 7 });
      // Fetch user details to check for company
      const userRes = await ApiService.apiUsersRetrieve({ id: userId });
      const { role } = userRes;
      const { company } = userRes;
      if ((role === "recruiter" || role === "smallbiz_owner") && !company) {
        navigate("/orgprofile");
      } else if (role === "candidate") {
        navigate("/home");
      } else {
        navigate("/orghome");
      }
    } catch (error) {
      console.error("Signup error:", error);

      const errorBody = error.body;
      const { status } = error;

      let message = "Failed to signup. Please try again.";

      if (status === 409) {
        message = "User already exists.";
      } else if (status === 400) {
        // Check if email field exists in body
        if (errorBody?.email && errorBody.email.length > 0) {
          message = "Email already exists."; // e.g., "user already exists"
        } else {
          message = "Bad request. Please check your input.";
        }
      }

      showAlert(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  const handleGoogleSuccess = async (response) => {
    try {
      const csrfToken = Cookies.get("csrftoken");
      const payload = { token: response.credential };
      const res = await axios.post("/api/googlelogin/", payload, {
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (res.status === 200 && res.data.user?.id) {
        const userId = res.data.user.id;
        Cookies.set("id", userId, { expires: 7 });

        const userRes = await ApiService.apiUsersRetrieve({ id: userId });
        const { role } = userRes;
        const { company } = userRes;
        Cookies.set("role", role, { expires: 7 });

        if ((role === "recruiter" || role === "smallbiz_owner") && !company) {
          navigate("/orgprofile");
        } else if (role === "candidate") {
          navigate("/home");
        } else {
          navigate("/orghome");
        }
      } else {
        showAlert("Failed to sign up with Google", "error");
      }
    } catch (error) {
      console.error("Google signup error:", error.response);
      const status = error.response?.status;
      const errorData = error.response?.data;

      if (status === 409) {
        showAlert(
          errorData?.detail ||
            "This email is already registered. Please log in.",
          "error",
        );
      } else {
        showAlert("Google signup failed. Please try again.", "error");
      }
    }
  };

  const handleGoogleFailure = () => showAlert("Google signup failed", "error");

  return (
    <GoogleOAuthProvider clientId="637231094750-2iviubrnmb70qmt9g89nr1nb1u6cqd43.apps.googleusercontent.com">
      <Navbar />
      <div className="bg-white dark:bg-gray-900 flex justify-center">
        <Transition
          appear
          enter="transition-all duration-700 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          show
        >
          <div className="p-4 flex flex-col items-center justify-center w-full max-w-4xl">
            <h3 className="m-8 text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 text-center">
              Sign up to hire talent fast
            </h3>
            <div className="flex flex-col px-6 md:px-10 py-4 md:py-6 w-full md:w-3/5 shadow-md rounded-md">
              {/* Google Signup */}
              <div className="mt-2 flex justify-center">
                <GoogleLogin
                  shape="pill"
                  size="large"
                  text="signup_with"
                  theme="filled_blue"
                  width="250"
                  onError={handleGoogleFailure}
                  onSuccess={handleGoogleSuccess}
                />
              </div>
              <div className="relative flex items-center justify-center my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <span className="relative bg-white dark:bg-gray-900 px-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                  or
                </span>
              </div>

              {/* Form */}
              <form
                className="flex flex-col gap-6"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="relative flex flex-col gap-y-2">
                  <label
                    className="text-sm font-medium text-gray-900 dark:text-gray-100"
                    htmlFor="username"
                  >
                    Full Name
                  </label>
                  <input
                    {...register("username")}
                    className="w-full h-10 p-2 rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 transition-all duration-200 bg-gray-50/50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    id="username"
                  />
                  {errors.username && (
                    <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                      {errors.username.message}
                    </p>
                  )}
                </div>
                <div className="relative flex flex-col gap-y-2">
                  <label
                    className="text-sm font-medium text-gray-900 dark:text-gray-100"
                    htmlFor="email"
                  >
                    Email Address
                  </label>
                  <input
                    {...register("email")}
                    className="w-full h-10 p-2 rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 transition-all duration-200 bg-gray-50/50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    id="email"
                  />
                  {errors.email && (
                    <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div className="relative flex flex-col gap-y-2">
                  <label
                    className="text-sm font-medium text-gray-900 dark:text-gray-100"
                    htmlFor="password"
                  >
                    Password
                  </label>
                  <input
                    {...register("password")}
                    className="w-full h-10 p-2 rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 transition-all duration-200 bg-gray-50/50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    id="password"
                    type={showPassword ? "text" : "password"}
                  />
                  <button
                    className="absolute right-3 top-12 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200"
                    type="button"
                    onClick={toggleShowPassword}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>
                {/* hidden role field */}
                <input type="hidden" {...register("role")} value={recRole} />

                <button
                  className="w-full h-[40px] cursor-pointer bg-blue-700 text-white font-medium rounded-lg shadow-md hover:bg-blue-600 disabled:bg-blue-300 transition-all duration-300 transform focus:outline-none focus:ring focus:ring-blue-400"
                  disabled={loading}
                  type="submit"
                >
                  {loading ? "Signing Up..." : "Sign Up"}
                </button>
              </form>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 text-center">
              Already have an account?{" "}
              <Link
                className="text-blue-700 dark:text-blue-400 font-semibold hover:text-blue-600 dark:hover:text-blue-300 transition-colors duration-200"
                to="/login"
              >
                Log In
              </Link>
            </p>
          </div>
        </Transition>
      </div>
    </GoogleOAuthProvider>
  );
}

//
